import { datadogRum } from '@datadog/browser-rum';
import { Typography } from '@mui/material';
import { messenger } from '@side/next-zendesk-sdk';
import { toast } from '@side/pantry';
import { addMonths, format } from 'date-fns';
import { useRouter } from 'next/router';
import { useState } from 'react';
import type { ReferralPostBody } from '@/common/hooks/referrals/types';
import { TXM_REFERRALS_PATH } from '@/constants';
import { coreApi } from '$api-clients/clients';
import { useCoreMutate } from '$api-clients/hooks';

export function useCreateReferral() {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const mutate = useCoreMutate();

  const createReferral = async (body: ReferralPostBody) => {
    setLoading(true);

    const today = new Date();
    const bodyWithDefaults: ReferralPostBody = {
      startDate: format(today, 'yyyy-MM-dd'),
      endDate: format(addMonths(today, 18), 'yyyy-MM-dd'),
      ...body,
    };

    try {
      const response = await coreApi.POST('/referrals', {
        body: bodyWithDefaults,
      });

      if (response.error !== undefined) {
        throw new Error(
          typeof response.error === 'string'
            ? response.error
            : response.error.message,
        );
      }

      await mutate(['/referrals']);

      toast('success', 'Referral created successfully');
      void router.push(`${TXM_REFERRALS_PATH}/${response.data.id}/agreement`);

      return { data: response.data };
    } catch (error) {
      datadogRum.addError(error, {
        message: 'Failed to create referral',
      });

      toast(
        'error',
        <>
          Failed to create a referral. Please try again or contact&nbsp;
          <Typography
            component="u"
            onClick={() => messenger.open()}
            color="inherit"
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
          >
            Side Support
          </Typography>
          &nbsp;for assistance.
        </>,
        { showCloseButton: true },
      );

      return { data: undefined, error };
    } finally {
      setLoading(false);
    }
  };

  return [createReferral, { loading }] as const;
}
